import { useAppDispatch, useAppSelector } from "../store";
import styles from "./groupDetail.module.scss";
import { ChangeEvent, lazy, Suspense, useCallback, useEffect, useState } from "react";
import {
  fetchGroupDetailAction,
  fetchGroupOwnerAction,
  getGroupCardAction,
  getGroupUserAction,
  connectGroupCardAction,
  connectGroupUserAction,
  deleteGroupCardAction,
  deleteGroupUserAction
} from "./groupDetailSlice";
import { useParams } from "react-router-dom";
import { Button, Avatar, FormControlLabel, FormGroup, Skeleton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { bgColorbyName, cardTypeColors, groupColors, roleColors } from "src/variable/randomColor";
import {
  activeStatusMapping,
  CAN_CONNECT_CARD_TO_GROUP,
  CAN_CONNECT_USER_TO_GROUP,
  CAN_CREATE_GROUP_WALLET,
  CAN_UPDATE_GROUP,
  cardTypeMapping,
  EMPTY_STRING,
  groupTypeMapping,
  PARTICIPANT,
  reverseActiveStatusMapping,
  roleMapping,
  SHARED
} from "src/components/constant/constant";
import IOSSwitch from "src/components/SettingCard/IOSSwitch";
import { toggleGroupStatus } from "src/network/graphql/groupService";
import { immediateToast } from "izitoast-react";
import swal from "sweetalert2";
import { CardContainer, MemberCard } from "src/components/groupComponent/editGroup/EditGroup";
import { checkPermission } from "src/utils/utils";
import { AddWalletIn } from "src/components/walletList/addWalletModal/AddWalletModal";
import { createGroupWalletAction } from "../group/groupSlice";
const Modalui = lazy(() => import("src/components/UI/modal/Modalui"));
const AddGroup = lazy(() => import("src/components/groupComponent/addGroup/AddGroup"));
const AttachCard = lazy(() => import("src/components/attachCard/AttachCard"));
const OrderCardModal = lazy(() => import("src/components/orderCardModal/OrderCardModal"));
const SupporterModal = lazy(() => import("src/components/connectCard/supporterModal/SupporterModal"));
const AddWalletModal = lazy(() => import("src/components/walletList/addWalletModal/AddWalletModal"));

export type ModalType = "UPDATE" | "ORDER" | "SUPPORTER" | "CARD" | "WALLET" | null;

export default function GroupDetail() {
  const dispatch = useAppDispatch();
  const { groupId } = useParams();
  const { groupDetail, groupCardLoading, groupUserLoading, groupCard, groupUser, groupOwner, loading } = useAppSelector(
    (state) => state.groupDetail
  );
  const createGroupLoading = useAppSelector((state) => state?.group?.createGroupLoading);
  const [isActiveChecked, setIsActiveChecked] = useState<boolean>(false);
  const [modalType, setModalType] = useState<ModalType>(null);

  function closeModal() {
    setModalType(null);
  }

  const fetchGroupDetail = useCallback(() => {
    if (!groupId) return;
    dispatch(fetchGroupDetailAction(groupId));
  }, [groupId]);

  const fetchGroupUser = useCallback(() => {
    if (!groupId) return;
    dispatch(getGroupUserAction(groupId));
  }, [groupId]);
  const fetchGroupCard = useCallback(() => {
    if (!groupId) return;
    dispatch(getGroupCardAction(groupId));
  }, [groupId]);

  useEffect(() => {
    if (groupDetail?.owner?.user_id) {
      dispatch(fetchGroupOwnerAction(groupDetail?.owner?.user_id));
    }
  }, [groupDetail?.owner?.user_id]);

  useEffect(() => {
    fetchGroupDetail();
    fetchGroupUser();
    fetchGroupCard();
  }, []);

  useEffect(() => {
    if (groupDetail) {
      setIsActiveChecked(activeStatusMapping[groupDetail?.group_status]);
    }
  }, [groupDetail]);

  const handleActiveToggle = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    swal
      .fire({
        title: `Are you sure! you want to ${reverseActiveStatusMapping[String(isChecked)].toLowerCase()} ${groupDetail?.name}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#1e293b",
        confirmButtonText: "Yes",
        customClass: {
          popup: "swal2-popup-custom"
        }
      })
      .then(async (result) => {
        if (result.isConfirmed) {
          setIsActiveChecked(isChecked);
          if (groupId) {
            const response = await toggleGroupStatus(groupId, isChecked);

            if (response.data.errors) {
              setIsActiveChecked(!isChecked);
            } else {
              immediateToast("success", {
                message: `${groupDetail?.name} is ${response.data.toggle_group_status.group_status.toLowerCase()}`,
                timeout: 3000,
                position: "topCenter"
              });
              setIsActiveChecked(activeStatusMapping[response.data.toggle_group_status.group_status]);
              fetchGroupDetail();
            }
          }
        }
      });
  };

  const deleteUser = (userId: string) => {
    if (!groupId) return;
    swal
      .fire({
        title: `Are you sure you want to remove user from ${groupDetail?.name}?`,
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#1e293b",
        confirmButtonText: "Yes, remove it!",
        customClass: {
          popup: "swal2-popup-custom"
        }
      })
      .then((result) => {
        if (result.isConfirmed) {
          dispatch(
            deleteGroupUserAction(groupId, userId, () => {
              fetchGroupUser();
            })
          );
        }
      });
  };

  const deleteCard = (cardId: string) => {
    if (!groupId) return;
    swal
      .fire({
        title: `Are you sure you want to remove this card from ${groupDetail?.name}?`,
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#1e293b",
        confirmButtonText: "Yes, remove it!",
        customClass: {
          popup: "swal2-popup-custom"
        }
      })
      .then((result) => {
        if (result.isConfirmed) {
          dispatch(
            deleteGroupCardAction(groupId, cardId, () => {
              fetchGroupCard();
            })
          );
        }
      });
  };

  const onAddCard = (_cardNumber: string, cardId: string, close: boolean) => {
    if (!groupId) return;
    dispatch(
      connectGroupCardAction(groupId, cardId, () => {
        if (close) closeModal();
        fetchGroupCard();
      })
    );
  };

  const onAddUser = (_email: string, userId: string, close: boolean) => {
    if (!groupId) return;
    dispatch(
      connectGroupUserAction(groupId, userId, () => {
        if (close) closeModal();
        fetchGroupUser();
      })
    );
  };

  const handleAddWallet = (walletData: AddWalletIn) => {
    if (!groupId) return;
    dispatch(
      createGroupWalletAction(walletData.description, walletData.walletName, groupId, () => {
        closeModal();
        fetchGroupDetail();
      })
    );
  };

  const renderModalContent = () => {
    switch (modalType) {
      case "UPDATE":
        return (
          <AddGroup
            update
            handleClose={closeModal}
            onSuccess={fetchGroupDetail}
            data={groupDetail}
            heading={`Update ${groupDetail?.name} group`}
            buttonText={{ primaryButton: "Update" }}
          />
        );
      case "ORDER":
        return (
          <OrderCardModal
            onSuccess={(cardId: string) => {
              onAddCard("", cardId, true);
            }}
            handleClose={closeModal}
            hideUserInput
            userDetail={groupOwner}
            CardType={{ type: SHARED, disable: true }}
          />
        );
      case "SUPPORTER":
        return (
          <SupporterModal
            handleClose={closeModal}
            participantName={groupDetail?.name}
            onAddSupporter={onAddUser}
            hideConnectToAll
            loading={groupUserLoading}
          />
        );
      case "CARD":
        return (
          <AttachCard
            handleClose={closeModal}
            participantName={groupDetail?.name}
            onAddSupporter={onAddCard}
            hideConnectToAll
            loading={groupCardLoading}
          />
        );
      case "WALLET":
        return (
          <AddWalletModal
            handleClose={closeModal}
            isWalletListEmpty={true}
            onAddWallet={handleAddWallet}
            walletloading={createGroupLoading}
            userId={groupDetail?.owner?.user_id}
            walletType={"GROUP"}
          />
        );
      default:
        return null;
    }
  };

  const getModalOverlayClass = (type: ModalType | null) => {
    switch (type) {
      case "UPDATE":
      case "WALLET":
        return styles.updateGroupModaluiOver;
      case "ORDER":
      case "SUPPORTER":
        return styles.modaluiOver;
      default:
        return styles.modaluiOver;
    }
  };

  return (
    <>
      <Suspense fallback={null}>
        <Modalui open={!!modalType} handleClose={closeModal} modaluiOver={getModalOverlayClass(modalType)}>
          {renderModalContent()}
        </Modalui>
      </Suspense>
      <div className={styles.groupPage}>
        <header className={styles.welCard}>
          <div className={styles.welCardL}>
            <div className={styles.userImage}>
              <Avatar
                id="av"
                className={styles.av}
                aria-label="Recipe"
                style={{ backgroundColor: bgColorbyName(groupDetail?.name || EMPTY_STRING) }}
              >
                {groupDetail.name?.charAt(0).toLocaleUpperCase()}
              </Avatar>
            </div>
            <div className={styles.userName}>
              <h3>{groupDetail?.name}</h3>
              <p>
                {groupDetail.description} | {groupDetail?.display_id}
              </p>
            </div>
          </div>
          <div className={styles.welCardR}>
            {checkPermission(CAN_CONNECT_USER_TO_GROUP) && (
              <Button variant="contained" onClick={() => setModalType("SUPPORTER")}>
                Connect User
              </Button>
            )}
          </div>
        </header>
        <main className={styles.gridCards}>
          <div className={styles.gridRowI}>
            <div className={styles.infoCard}>
              <div className={styles.head}>
                <div className={styles.headL}>
                  <h3>Group information</h3>
                  <p>Edit the information about your group.</p>
                </div>
                <div className={styles.headR}>
                  {checkPermission(CAN_UPDATE_GROUP) && groupDetail?.can_update_group && (
                    <div className={styles.editIconContainer} onClick={() => setModalType("UPDATE")}>
                      <EditIcon className={styles.editIcon} aria-label="Edit Group Information" />
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.items}>
                <div className={styles.item}>
                  <span className={styles.heading}>Name : </span>
                  <span className={styles.value}>{groupDetail?.name}</span>
                </div>
                <div className={styles.item}>
                  <span className={styles.heading}>Description : </span>
                  <span className={styles.value}>{groupDetail?.description}</span>
                </div>
                <div className={styles.item}>
                  <span className={styles.heading}>Type : </span>
                  <div
                    className={styles.role}
                    style={{
                      backgroundColor: `${groupColors[groupDetail?.group_type]}33`,
                      borderColor: `${groupColors[groupDetail?.group_type]}73`,
                      color: `${groupColors[groupDetail?.group_type]}FF`
                    }}
                  >
                    {groupTypeMapping[groupDetail?.group_type]}
                  </div>
                </div>
                <div className={styles.item}>
                  <label className={styles.heading}> Active :</label>
                  <FormGroup className={styles.formGroup} style={{ paddingTop: "4px", marginLeft: "4px" }}>
                    <FormControlLabel
                      className={styles.formControlLabel}
                      control={<IOSSwitch sx={{ m: 1 }} checked={isActiveChecked} onChange={handleActiveToggle} />}
                      label={EMPTY_STRING}
                      aria-label="active"
                    />
                  </FormGroup>
                </div>
                <div className={styles.item}>
                  <span className={styles.heading}>Owner : </span>
                  <MemberCard
                    link={`/users/${groupDetail?.owner?.user_id}`}
                    name={groupDetail?.owner?.attributes?.first_name + " " + groupDetail?.owner?.attributes?.last_name}
                    secondaryInfo={groupDetail?.owner?.email}
                  />
                </div>
                {/* <div className={styles.item}>
                  <span className={styles.heading}>Wallet : </span>
                  <MemberCard
                    link={`/wallets/${groupDetail?.wallets[0]?.wallet_id}`}
                    name={groupDetail?.wallets[0]?.name}
                    secondaryInfo={groupDetail?.wallets[0]?.description}
                  />
                </div> */}
              </div>
            </div>
            <CardContainer
              title="Group Wallet"
              buttons={{
                primaryButton: {
                  text: "Create Wallet",
                  visible: checkPermission(CAN_CREATE_GROUP_WALLET),
                  onClick: () => {
                    setModalType("WALLET");
                  }
                }
              }}
            >
              <div className={styles.userMembers}>
                {loading ? (
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      borderRadius: "5px",
                      height: "45px",
                      width: "-webkit-fill-available"
                    }}
                  />
                ) : (
                  (() => {
                    const wallets = groupDetail?.wallets || [];

                    return wallets.length > 0 ? (
                      wallets.map((wallet) => (
                        <MemberCard
                          key={wallet?.wallet_id}
                          link={`/wallets/${wallet?.wallet_id}`}
                          name={wallet?.name}
                          secondaryInfo={wallet?.description}
                          // role={cardTypeMapping[card?.accessType]}
                          // roleColorValue={cardTypeColors[card?.accessType]}
                          // {...(checkPermission(CAN_CONNECT_CARD_TO_GROUP) && {
                          //   onDelete: () => deleteCard(card?.id)
                          // })}
                        />
                      ))
                    ) : (
                      <p className={styles.noData}>No Wallet Connected</p>
                    );
                  })()
                )}
              </div>
            </CardContainer>
            <CardContainer
              title="Card Connect"
              buttons={{
                primaryButton: {
                  text: "Connect card",
                  visible: checkPermission(CAN_CONNECT_CARD_TO_GROUP),
                  onClick: () => {
                    setModalType("CARD");
                  }
                },
                secondaryButton: {
                  text: "Order card",
                  visible: checkPermission(CAN_CONNECT_CARD_TO_GROUP),
                  onClick: () => {
                    setModalType("ORDER");
                  }
                }
              }}
            >
              <div className={styles.userMembers}>
                {groupCardLoading ? (
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      borderRadius: "5px",
                      height: "45px",
                      width: "-webkit-fill-available"
                    }}
                  />
                ) : (
                  (() => {
                    const cards = groupCard?.list || [];

                    return cards.length > 0 ? (
                      cards.map((card) => (
                        <MemberCard
                          key={card?.id}
                          name={card?.cardHolderName ?? card?.user?.firstName + " " + card?.user?.lastName}
                          secondaryInfo={card?.cardNumber ? "****" + card?.cardNumber.slice(-4) : EMPTY_STRING}
                          role={cardTypeMapping[card?.accessType]}
                          roleColorValue={cardTypeColors[card?.accessType]}
                          {...(checkPermission(CAN_CONNECT_CARD_TO_GROUP) && {
                            onDelete: () => deleteCard(card?.id)
                          })}
                        />
                      ))
                    ) : (
                      <p className={styles.noData}>No Card Connected</p>
                    );
                  })()
                )}
              </div>
            </CardContainer>
          </div>
          <div className={styles.gridRowII}>
            <CardContainer title="Participants">
              <div className={styles.userMembers}>
                {groupUserLoading ? (
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      borderRadius: "5px",
                      height: "45px",
                      width: "-webkit-fill-available"
                    }}
                  />
                ) : (
                  (() => {
                    const participants =
                      groupUser?.list?.filter((u) => u.current_organisation_role === PARTICIPANT) || [];

                    return participants.length > 0 ? (
                      participants.map((user) => (
                        <MemberCard
                          key={user.user_id}
                          link={`/users/${user.user_id}`}
                          name={`${user.attributes?.first_name} ${user.attributes?.last_name}`}
                          secondaryInfo={user.email}
                          {...(checkPermission(CAN_CONNECT_USER_TO_GROUP) && {
                            onDelete: () => deleteUser(user.user_id)
                          })}
                        />
                      ))
                    ) : (
                      <p className={styles.noData}>No Participant Connected</p>
                    );
                  })()
                )}
              </div>
            </CardContainer>
            <CardContainer title="User Connect">
              <div className={styles.userMembers}>
                {groupUserLoading ? (
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      borderRadius: "5px",
                      height: "45px",
                      width: "-webkit-fill-available"
                    }}
                  />
                ) : (
                  (() => {
                    const users = groupUser?.list?.filter((u) => u.current_organisation_role !== PARTICIPANT) || [];

                    return users.length > 0 ? (
                      users.map((user) => (
                        <MemberCard
                          key={user.user_id}
                          link={`/users/${user.user_id}`}
                          name={`${user.attributes?.first_name} ${user.attributes?.last_name}`}
                          secondaryInfo={user.email}
                          role={roleMapping[user.current_organisation_role]}
                          roleColorValue={roleColors[user.current_organisation_role]}
                          {...(checkPermission(CAN_CONNECT_USER_TO_GROUP) && {
                            onDelete: () => deleteUser(user.user_id)
                          })}
                        />
                      ))
                    ) : (
                      <p className={styles.noData}>No User Connected</p>
                    );
                  })()
                )}
              </div>
            </CardContainer>
          </div>
        </main>
      </div>
    </>
  );
}
